import { useState, Fragment } from 'react';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import PopoverMenu from '@/common/PopoverMenu';
import { useActivePlayer } from '@/context/ActivePlayerContext';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import EditPlayerInfo from './EditPlayerInfo';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { isEmptyTile, isMobileResolution, normalizeString } from '@/utils';
import GreenCheckmarkIcon from '@/assets/icons/green-checkmark.svg?react';
import clubStyles from '../Club.module.scss';
import styles from './PlayerInfo.module.scss';
import Linkify from 'linkify-react';

type PlayerInfoNavState = {
  editMode?: boolean;
};

export const sortByDefault = <T extends { isDefault: boolean }>(a: T, b: T) => {
  return (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0); // true first
};

export default function PlayerInfo() {
  const location = useLocation();
  const { player, permissions } = useActivePlayer();
  const { isSyncComplete } = useBackgroundSync();
  const isOnline = useOnlineStatus();
  const navState = location.state as PlayerInfoNavState | null;
  const [editPlayerInfo, setEditPlayerInfo] = useState(Boolean(navState?.editMode));
  const dob = normalizeString(player?.playerBioDto?.dob ? dayjs(player?.playerBioDto?.dob).format('MM/DD/YYYY') : '');
  const handleEdit = () => setEditPlayerInfo(true);
  const userHasEditPermission = permissions?.tourPlayerSettings?.canEditPlayer && permissions?.tourPageSettings?.playerBio === 'full';
  const currentPlayerRanking = player?.currentPlayerRanking;

  return (
    <div className={`${clubStyles.container} ${editPlayerInfo ? clubStyles.editingBackground : ''}`}>
      {editPlayerInfo && !isMobileResolution && <EditPlayerInfo handleCancel={() => setEditPlayerInfo(false)} />}
      {!editPlayerInfo && (
        <div className={styles.container} data-testid="player-info-view-root">
          {!isMobileResolution && userHasEditPermission && isSyncComplete && isOnline && (
            <div className={styles.menu}>
              <PopoverMenu index={0} onEdit={handleEdit} showHistory={false} testId="player-info-menu-trigger" />
            </div>
          )}
          <div className={styles.form}>
            <div className={styles.row}>
              <div className={clubStyles.title}>Player</div>
            </div>
            <div className={styles.tileRow}>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>First Name</div>
                <div className={`${styles.tileValue} ${isEmptyTile(player?.firstName || '') ? styles.tileEmpty : ''}`}>{normalizeString(player?.firstName || '')}</div>
              </div>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Last Name</div>
                <div className={`${styles.tileValue} ${isEmptyTile(player?.lastName || '') ? styles.tileEmpty : ''}`}>{normalizeString(player?.lastName || '')}</div>
              </div>
            </div>
            <div className={styles.row}>
              <div className={clubStyles.title}>Designation</div>
            </div>
            <div className={styles.tileRow}>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Account</div>
                <div className={`${styles.tileValue} ${isEmptyTile(player?.defaultTourName || '') ? styles.tileEmpty : ''}`}>{normalizeString(player?.defaultTourName || '')}</div>
              </div>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Player Status</div>
                <div className={`${styles.tileValue} ${isEmptyTile(player?.playerBioDto?.playerStatusLookupName || '') ? styles.tileEmpty : ''}`}>
                  {normalizeString(player?.playerBioDto?.playerStatusLookupName || '')}
                </div>
              </div>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Internal Status</div>
                <div className={`${styles.tileValue} ${isEmptyTile(player?.playerBioDto?.playerInternalStatusLookupName || '') ? styles.tileEmpty : ''}`}>
                  {normalizeString(player?.playerBioDto?.playerInternalStatusLookupName || '')}
                </div>
              </div>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Region</div>
                <div className={`${styles.tileValue} ${isEmptyTile(player?.playerBioDto?.playerRegionLookupName || '') ? styles.tileEmpty : ''}`}>
                  {normalizeString(player?.playerBioDto?.playerRegionLookupName || '')}
                </div>
              </div>
            </div>
            <div className={styles.row}>
              <div className={clubStyles.title}>Address</div>
            </div>
            <div className={styles.tileRow}>
              {player?.addresses?.sort(sortByDefault).map((address, index) => (
                <Fragment key={`${address.addressId}-${index}`}>
                  <div className={`${styles.tile} + ${styles.address}`}>
                    <span className={`${styles.tileCode} ${styles.tileTitle}`}>
                      {address.code} {address.isDefault ? <GreenCheckmarkIcon /> : ''}
                    </span>
                    <div className={styles.tileText}>{address.address1}</div>
                    <div className={styles.tileText}>{address.address2}</div>
                    <div className={styles.tileText}>
                      {address.city} {address.state}, {address.postalCode}
                    </div>
                    <div className={styles.tileText}>{address.country}</div>
                  </div>
                </Fragment>
              ))}
            </div>
            <div className={styles.row}>
              <div className={clubStyles.title}>Phone</div>
            </div>
            <div className={styles.tileRow}>
              {player?.phonesResponse?.sort(sortByDefault).map((phone, index) => (
                <Fragment key={`${phone.phoneId}-${index}`}>
                  <div className={`${styles.tile}`}>
                    <span className={`${styles.tileCode} ${styles.tileTitle}`}>
                      {phone.code} {phone.isDefault ? <GreenCheckmarkIcon /> : ''}
                    </span>
                    <div className={styles.tileText}>{phone.phoneNo}</div>
                  </div>
                </Fragment>
              ))}
            </div>
            <div className={styles.row}>
              <div className={clubStyles.title}>General</div>
            </div>
            <div className={styles.tileRow}>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Birthday</div>
                <div className={`${styles.tileValue} ${isEmptyTile(dob) ? styles.tileEmpty : ''}`}>{dob}</div>
              </div>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Email</div>
                <div className={`${styles.tileValue} ${isEmptyTile(player?.playerBioDto?.email || '') ? styles.tileEmpty : ''}`}>{normalizeString(player?.playerBioDto?.email || '')}</div>
              </div>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Nationality</div>
                <div className={`${styles.tileValue} ${isEmptyTile(player?.playerBioDto?.nationality || '') ? styles.tileEmpty : ''}`}>{normalizeString(player?.playerBioDto?.nationality || '')}</div>
              </div>
            </div>
            <div className={styles.tileRow}>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Manager</div>
                <div className={`${styles.tileValue} ${isEmptyTile(player?.playerBioDto?.manager || '') ? styles.tileEmpty : ''}`}>{normalizeString(player?.playerBioDto?.manager || '')}</div>
              </div>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Caddy</div>
                <div className={`${styles.tileValue} ${isEmptyTile(player?.playerBioDto?.caddy || '') ? styles.tileEmpty : ''}`}>{normalizeString(player?.playerBioDto?.caddy || '')}</div>
              </div>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>College</div>
                <div className={`${styles.tileValue} ${isEmptyTile(player?.playerBioDto?.playerCollegeLookupName || '') ? styles.tileEmpty : ''}`}>
                  {normalizeString(player?.playerBioDto?.playerCollegeLookupName || '')}
                </div>
              </div>
              <div className={`${styles.tile} + ${styles.small}`}>
                <div className={styles.tileLabel}>Year</div>
                <div className={`${styles.tileValue} ${isEmptyTile(player?.playerBioDto?.playerYearLookupName || '') ? styles.tileEmpty : ''}`}>
                  {normalizeString(player?.playerBioDto?.playerYearLookupName || '')}
                </div>
              </div>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Support</div>
                <div className={`${styles.tileValue} ${isEmptyTile(player?.playerBioDto?.playerSupportLookupName || '') ? styles.tileEmpty : ''}`}>
                  {normalizeString(player?.playerBioDto?.playerSupportLookupName || '')}
                </div>
              </div>
            </div>
            <div className={styles.row}>
              <div className={clubStyles.title}>FedEx Cup and OWGR</div>
            </div>
            <div className={styles.tileRow}>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>FedEx Cup Rank</div>
                <div className={`${styles.tileValue} ${isEmptyTile(normalizeString(currentPlayerRanking?.fedexCupRank)) ? styles.tileEmpty : ''}`}>
                  {normalizeString(currentPlayerRanking?.fedexCupRank)}
                </div>
              </div>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Events Played</div>
                <div className={`${styles.tileValue} ${isEmptyTile(normalizeString(currentPlayerRanking?.fedexCupEventsPlayed)) ? styles.tileEmpty : ''}`}>
                  {normalizeString(currentPlayerRanking?.fedexCupEventsPlayed)}
                </div>
              </div>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Points</div>
                <div className={`${styles.tileValue} ${isEmptyTile(normalizeString(currentPlayerRanking?.fedexCupPoints)) ? styles.tileEmpty : ''}`}>
                  {normalizeString(currentPlayerRanking?.fedexCupPoints)}
                </div>
              </div>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Wins</div>
                <div className={`${styles.tileValue} ${isEmptyTile(normalizeString(currentPlayerRanking?.fedexCupWins)) ? styles.tileEmpty : ''}`}>
                  {normalizeString(currentPlayerRanking?.fedexCupWins)}
                </div>
              </div>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Top 10s</div>
                <div className={`${styles.tileValue} ${isEmptyTile(normalizeString(currentPlayerRanking?.fedexCupTop10s)) ? styles.tileEmpty : ''}`}>
                  {normalizeString(currentPlayerRanking?.fedexCupTop10s)}
                </div>
              </div>
            </div>
            <div className={styles.tileRow}>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>OWGR Rank</div>
                <div className={`${styles.tileValue} ${isEmptyTile(normalizeString(currentPlayerRanking?.owgrRank)) ? styles.tileEmpty : ''}`}>{normalizeString(currentPlayerRanking?.owgrRank)}</div>
              </div>
              <div className={`${styles.tile} + ${styles.medium}`}>
                <div className={styles.tileLabel}>Events</div>
                <div className={`${styles.tileValue} ${isEmptyTile(normalizeString(currentPlayerRanking?.owgrEventsPlayed)) ? styles.tileEmpty : ''}`}>
                  {normalizeString(currentPlayerRanking?.owgrEventsPlayed)}
                </div>
              </div>
            </div>
            <div className={styles.row}>
              <div className={clubStyles.title}>Player Notes</div>
            </div>
            <div className={styles.tileRow}>
              <div className={`${styles.tile} ${styles.fullWidth} ${styles.playerNotesTile}`}>
                <div className={`${styles.tileValue} ${styles.playerNotesValue} ${isEmptyTile(player?.playerBioDto?.notes || '') ? styles.tileEmpty : ''}`}>
                  <Linkify options={{ target: '_blank' }}>{normalizeString(player?.playerBioDto?.notes || '')}</Linkify>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
